import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { Tooltip } from "@msuite/ui/tooltip";
import type { ApprovalTurn } from "@msuite/utils/incoming-invoices";
import React, { useState } from "react";
import { ReviewIncomingInvoiceModal } from "@/modals/incoming-invoices/review-incoming-invoice-modal";

/** Props Interface */
interface ReviewApprovalButtonProps {
	incomingInvoiceId: string;
	approvalId: string;
	/** Where the approval stands in its invoice's chain. */
	turn: ApprovalTurn;
	/** A default approval, which may decide ahead of the open chain once that is confirmed. */
	mayDecideAhead: boolean;
	/** How many open steps deciding ahead of the chain skips. */
	skipCount: number;
}

/**
 * The one action an approver takes on an invoice: "Prüfen". Open on the approval's turn; a
 * default approver waiting on the chain may decide ahead of it after confirming the skip, anyone
 * else waits. Renders nothing once the approval is decided or out of the chain.
 */
export function ReviewApprovalButton({
	incomingInvoiceId,
	approvalId,
	turn,
	mayDecideAhead,
	skipCount,
}: ReviewApprovalButtonProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const modal = useDisclosure();
	const confirmModal = useAsyncModal();

	/** State */
	const [skipChain, setSkipChain] = useState(false);

	/** Functions */
	async function handleOnReview() {
		const isAhead = turn === "waiting";
		if (isAhead && !(await confirmModal.confirm())) return;
		setSkipChain(isAhead);
		modal.onOpen();
	}

	/** Computed */
	const isWaiting = turn === "waiting" && !mayDecideAhead;

	/** Guard */
	if (turn !== "now" && turn !== "waiting") return null;

	/** Render */
	return (
		<React.Fragment>
			<Tooltip
				label={t("incoming_invoices.decision.waiting")}
				isDisabled={!isWaiting}
			>
				<Button
					icon={<Icon name="ClipboardCheck" />}
					size="xxs"
					variant="white"
					isDisabled={isWaiting}
					onClick={handleOnReview}
					data-cy="review-incoming-invoice-button"
				>
					{t("incoming_invoices.decision.review")}
				</Button>
			</Tooltip>
			{confirmModal.isOpen && (
				<ConfirmModal
					title={t("incoming_invoices.decision.skip_chain_title")}
					message={t("incoming_invoices.decision.skip_chain_message", { count: `${skipCount}` })}
					controller={confirmModal}
				/>
			)}
			<ModalWrapper>
				{modal.isOpen && (
					<ReviewIncomingInvoiceModal
						incomingInvoiceId={incomingInvoiceId}
						approvalId={approvalId}
						skipChain={skipChain}
						isOpen={modal.isOpen}
						onClose={modal.onClose}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
